import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useUi } from '../context/UiContext';
import { calculateLeadQualityScore } from '../scoring';
import ScoreDisplay from '../components/ScoreDisplay';

const EMPTY = {
  full_name: '',
  phone: '',
  email: '',
  location: '',
  service_required: '',
  description: '',
  budget: '',
  expected_timeline: '',
  preferred_date: '',
  message: '',
  customer_type: '',

  verify_phone_valid: '',
  verify_phone_reachable: 'not_checked',
  verify_location: '',
  verify_customer_type: '',
  verify_requirement: '',
  verify_actual_requirement: 'not_checked',
  verify_budget_timeline: '',
  verify_shows_interest: 'not_checked',
  verify_relevant_questions: 'not_checked',

  sales_appointment_scheduled: 'no',
  sales_opportunity_generated: 'no',
  sales_converted: 'no',
};

const REQUIRED = ['full_name', 'phone', 'location', 'service_required', 'description'];

export default function LeadForm() {
  const { brandId, leadId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useUi();

  const [brand, setBrand] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const isEdit = Boolean(leadId);

  useEffect(() => {
    api.getBrand(brandId).then(setBrand).catch(() => navigate('/lead-quality-score'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  useEffect(() => {
    if (leadId) {
      api.getLead(leadId).then((lead) => setForm({ ...EMPTY, ...lead })).catch(() => {});
    } else {
      setForm(EMPTY);
    }
  }, [leadId]);

  const result = useMemo(() => {
    if (!brand) return null;
    return calculateLeadQualityScore(form, brand);
  }, [form, brand]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async () => {
    const nextErrors = {};
    REQUIRED.forEach((f) => {
      if (!form[f]) nextErrors[f] = true;
    });
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      showToast('Please fill in all required fields.', 'error');
      return;
    }

    const payload = { ...form, brand: brandId };
    try {
      if (isEdit) {
        await api.updateLead(leadId, payload);
        showToast('Lead updated successfully!', 'success');
      } else {
        await api.createLead(payload);
        showToast('Lead added successfully!', 'success');
      }
      navigate(`/lead-quality-score/brands/${brandId}`);
    } catch (err) {
      showToast('Failed to save lead. Is the backend running?', 'error');
    }
  };

  if (!brand) return null;

  return (
    <div className="container page-content">
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <button className="btn btn-ghost btn-sm mb-16" onClick={() => navigate(`/lead-quality-score/brands/${brandId}`)}>
              <BackIcon /> Back to Brand
            </button>
            <h2>{isEdit ? 'Edit Lead' : 'Add Lead'}</h2>
            <p className="mt-4">Fill in the lead details and verification data. The quality score updates in real time.</p>
          </div>
        </div>
      </div>

      <div className="lead-form-layout">
        <div>
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Section 01: Lead Details */}
            <Section index="01" title="Lead Details" desc="Basic contact and enquiry information">
              <div className="form-grid">
                <Field label="Full Name" required error={errors.full_name}>
                  <input className="form-control" placeholder="e.g. John Doe" value={form.full_name} onChange={set('full_name')} />
                </Field>
                <Field label="Phone Number" required error={errors.phone}>
                  <input className="form-control" placeholder="e.g. +91 98765 43210" value={form.phone} onChange={set('phone')} />
                </Field>
                <Field label="Email Address">
                  <input className="form-control" placeholder="e.g. john@example.com" value={form.email} onChange={set('email')} />
                </Field>
                <Field label="Location" required error={errors.location}>
                  <input className="form-control" placeholder="e.g. Coimbatore, Tamil Nadu" value={form.location} onChange={set('location')} />
                </Field>
                <Field label="Service / Product Required" required error={errors.service_required} span2>
                  <input className="form-control" placeholder="e.g. Interior Design, Digital Marketing" value={form.service_required} onChange={set('service_required')} />
                </Field>
                <Field label="Requirement Description" required error={errors.description} span2>
                  <textarea className="form-control" placeholder="Describe what the lead is looking for..." value={form.description} onChange={set('description')} />
                </Field>
                <Field label="Budget / Budget Range">
                  <input className="form-control" placeholder="e.g. ₹50,000 – ₹1,00,000" value={form.budget} onChange={set('budget')} />
                </Field>
                <Field label="Expected Purchase Timeline">
                  <select className="form-control" value={form.expected_timeline} onChange={set('expected_timeline')}>
                    <option value="">— Select —</option>
                    <option value="Immediate">Immediate (within 1 week)</option>
                    <option value="Short-term">Short-term (1–4 weeks)</option>
                    <option value="Mid-term">Mid-term (1–3 months)</option>
                    <option value="Long-term">Long-term (3–6 months)</option>
                    <option value="Exploratory">Exploratory / No urgency</option>
                  </select>
                </Field>
                <Field label="Preferred Date">
                  <input className="form-control" type="date" value={form.preferred_date} onChange={set('preferred_date')} />
                </Field>
                <div className="form-group">
                  <label className="form-label">Customer Type</label>
                  <div className="pill-group">
                    {['Individual', 'Business'].map((opt) => (
                      <Fragment key={opt}>
                        <input
                          type="radio"
                          className="pill-option"
                          id={`lead-customer-type-${opt}`}
                          checked={form.customer_type === opt}
                          onChange={() => setForm((f) => ({ ...f, customer_type: opt }))}
                        />
                        <label htmlFor={`lead-customer-type-${opt}`}>{opt}</label>
                      </Fragment>
                    ))}
                  </div>
                </div>
                <Field label="Additional Questions / Message" span2>
                  <textarea className="form-control" placeholder="Any additional notes or questions from the lead..." value={form.message} onChange={set('message')} />
                </Field>
              </div>
            </Section>

            {/* Section 02: Contactability */}
            <Section index="02" title="Contactability" desc="Phone validation — score: 20 pts">
              <div className="flex flex-col gap-16">
                <VerifyField
                  label="Valid Phone Number" hint="(auto-detected — override below)"
                  options={PHONE_AUTO_OPTIONS} value={form.verify_phone_valid}
                  onChange={(v) => setForm((f) => ({ ...f, verify_phone_valid: v }))}
                  footnote="Auto-detected from phone format. Override if needed. (+10 pts)"
                />
                <VerifyField
                  label="Phone Reachable?"
                  options={SIMPLE_OPTIONS} value={form.verify_phone_reachable}
                  onChange={(v) => setForm((f) => ({ ...f, verify_phone_reachable: v }))}
                  footnote="Did you successfully reach this contact? (+10 pts)"
                />
              </div>
            </Section>

            {/* Section 03: Target Fit */}
            <Section index="03" title="Target Fit" desc="Location, customer type, relevance — score: 30 pts">
              <div className="flex flex-col gap-16">
                <VerifyField
                  label="Location Serviceable?" hint="(auto-compared with brand)"
                  options={AUTO_OPTIONS} value={form.verify_location}
                  onChange={(v) => setForm((f) => ({ ...f, verify_location: v }))}
                  footnote="Auto-compared with brand's target location. (+10 pts)"
                />
                <VerifyField
                  label="Customer Type Match?" hint="(auto-compared with brand)"
                  options={AUTO_OPTIONS} value={form.verify_customer_type}
                  onChange={(v) => setForm((f) => ({ ...f, verify_customer_type: v }))}
                  footnote="Auto-compared with brand's target customer type. (+10 pts)"
                />
                <VerifyField
                  label="Relevant Requirement?" hint="(auto-compared with brand)"
                  options={AUTO_OPTIONS} value={form.verify_requirement}
                  onChange={(v) => setForm((f) => ({ ...f, verify_requirement: v }))}
                  footnote="Auto-compared with brand's primary product/service. (+10 pts)"
                />
              </div>
            </Section>

            {/* Section 04: Purchase Intent */}
            <Section index="04" title="Purchase Intent" desc="Need, budget, interest — score: 30 pts">
              <div className="flex flex-col gap-16">
                <VerifyField
                  label="Actual Requirement Confirmed?"
                  options={SIMPLE_OPTIONS} value={form.verify_actual_requirement}
                  onChange={(v) => setForm((f) => ({ ...f, verify_actual_requirement: v }))}
                  footnote="Does the lead have a genuine, confirmed need? (+10 pts)"
                />
                <VerifyField
                  label="Suitable Timeline / Budget?" hint="(auto-compared, override if needed)"
                  options={AUTO_OPTIONS} value={form.verify_budget_timeline}
                  onChange={(v) => setForm((f) => ({ ...f, verify_budget_timeline: v }))}
                  footnote="Budget and timeline aligned with brand criteria? (+10 pts)"
                />
                <VerifyField
                  label="Shows Interest?"
                  options={SIMPLE_OPTIONS} value={form.verify_shows_interest}
                  onChange={(v) => setForm((f) => ({ ...f, verify_shows_interest: v }))}
                  footnote="Is the lead genuinely interested in your product/service? (+10 pts)"
                />
              </div>
            </Section>

            {/* Section 05: Sales Progression */}
            <Section index="05" title="Sales Progression" desc="Pipeline stage tracking — score: 20 pts">
              <div className="flex flex-col gap-16">
                <VerifyField
                  label="Asks Relevant Questions?"
                  options={SIMPLE_OPTIONS} value={form.verify_relevant_questions}
                  onChange={(v) => setForm((f) => ({ ...f, verify_relevant_questions: v }))}
                  footnote="Did the lead ask relevant, informed questions? (+5 pts)"
                />
                <VerifyField
                  label="Appointment / Meeting Scheduled?"
                  options={YES_NO_OPTIONS} value={form.sales_appointment_scheduled}
                  onChange={(v) => setForm((f) => ({ ...f, sales_appointment_scheduled: v }))}
                  footnote="Has a meeting or appointment been confirmed? (+5 pts)"
                />
                <VerifyField
                  label="Opportunity / Quotation Generated?"
                  options={YES_NO_OPTIONS} value={form.sales_opportunity_generated}
                  onChange={(v) => setForm((f) => ({ ...f, sales_opportunity_generated: v }))}
                  footnote="Has a quote or opportunity been formally created? (+5 pts)"
                />
                <VerifyField
                  label="Converted?"
                  options={YES_NO_OPTIONS} value={form.sales_converted}
                  onChange={(v) => setForm((f) => ({ ...f, sales_converted: v }))}
                  footnote="Has this lead been converted to a paying customer? (+5 pts)"
                />
              </div>
            </Section>

            <div className="flex gap-12 mt-24">
              <button type="button" className="btn btn-ghost btn-lg" onClick={() => navigate(`/lead-quality-score/brands/${brandId}`)}>Cancel</button>
              <button type="button" className="btn btn-primary btn-lg w-full" onClick={submit}>
                {isEdit ? 'Update Lead' : 'Save Lead'}
              </button>
            </div>
          </form>
        </div>

        {/* Live Score Sidebar */}
        <div className="score-sidebar">
          <div className="score-sidebar-card">
            <div className="score-sidebar-top">
              <div className="score-sidebar-title">Live Quality Score</div>
              <ScoreDisplay score={result.totalScore} max={100} />
              <div className={`score-classification ${result.classification.cls}`} style={{ marginTop: 4 }}>
                {result.classification.label}
              </div>
            </div>

            <div style={{ padding: '20px 20px 0' }}>
              <SubScores marketing={result.marketingScore} sales={result.salesScore} />
              <div className="divider" />
              <div style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-ink-faint)', marginBottom: 10 }}>
                Check Breakdown
              </div>
              <Breakdown checks={result.checks} style={{ paddingBottom: 20 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const PHONE_AUTO_OPTIONS = [
  { value: '', label: 'Auto' },
  { value: 'satisfied', label: '✓ Valid' },
  { value: 'partial', label: '~ Partially' },
  { value: 'not_satisfied', label: '✕ Invalid' },
];

const AUTO_OPTIONS = [
  { value: '', label: 'Auto' },
  { value: 'satisfied', label: '✓ Yes' },
  { value: 'partial', label: '~ Partially' },
  { value: 'not_satisfied', label: '✕ No' },
];

const SIMPLE_OPTIONS = [
  { value: 'not_checked', label: 'Not Checked' },
  { value: 'yes', label: '✓ Yes' },
  { value: 'partial', label: '~ Partially' },
  { value: 'no', label: '✕ No' },
];

const YES_NO_OPTIONS = [
  { value: 'no', label: '✕ No' },
  { value: 'yes', label: '✓ Yes' },
];

function VerifyField({ label, hint, options, value, onChange, footnote }) {
  return (
    <div className="form-group">
      <label className="form-label">
        {label} {hint && <span style={{ color: 'var(--color-ink-faint)', fontWeight: 400 }}>{hint}</span>}
      </label>
      <div className="verify-group">
        {options.map((opt) => {
          const id = `${label}-${opt.value || 'auto'}`.replace(/[^a-zA-Z0-9]+/g, '-');
          return (
            <Fragment key={opt.value}>
              <input
                type="radio"
                className="verify-option"
                id={id}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
              />
              <label htmlFor={id}>{opt.label}</label>
            </Fragment>
          );
        })}
      </div>
      <span className="form-hint">{footnote}</span>
    </div>
  );
}

function Section({ index, title, desc, children }) {
  return (
    <div className="form-section">
      <div className="form-section-header">
        <div className="form-section-icon">{index}</div>
        <div>
          <div className="form-section-title">{title}</div>
          <div className="form-section-desc">{desc}</div>
        </div>
      </div>
      <div className="form-section-body">{children}</div>
    </div>
  );
}

function Field({ label, required, error, span2, children }) {
  return (
    <div className={`form-group${span2 ? ' col-span-2' : ''}${error ? ' has-error' : ''}`}>
      <label className="form-label">{label} {required && <span className="required">*</span>}</label>
      {children}
      {error && <span className="form-error" style={{ display: 'block' }}>This field is required.</span>}
    </div>
  );
}

export function SubScores({ marketing, sales }) {
  const mktPct = (marketing / 80) * 100;
  const salePct = (sales / 20) * 100;
  return (
    <div>
      <SubScoreBar label="Marketing" earned={marketing} max={80} pct={mktPct} />
      <div style={{ marginTop: 10 }}>
        <SubScoreBar label="Sales" earned={sales} max={20} pct={salePct} />
      </div>
    </div>
  );
}

function SubScoreBar({ label, earned, max, pct }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
        <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-ink-faint)' }}>{label}</span>
        <span style={{ fontSize: '0.8rem', fontWeight: 700 }}>{earned}/{max}</span>
      </div>
      <div className="sub-score-bar">
        <div className="sub-score-fill" style={{ width: `${pct}%`, background: scoreColor(pct) }} />
      </div>
    </div>
  );
}

function scoreColor(pct) {
  if (pct >= 80) return 'var(--green)';
  if (pct >= 60) return 'var(--yellow)';
  if (pct >= 40) return 'var(--orange)';
  return 'var(--red)';
}

export function Breakdown({ checks }) {
  const groups = [
    { title: 'Contactability', items: ['phoneValid', 'phoneReachable'] },
    { title: 'Target Fit', items: ['location', 'customerType', 'requirement'] },
    { title: 'Purchase Intent', items: ['actualRequirement', 'budgetTimeline', 'showsInterest'] },
    { title: 'Sales', items: ['relevantQuestions', 'appointment', 'opportunity', 'converted'] },
  ];
  const icons = { satisfied: '✓', partial: '~', failed: '✕', pending: '○' };
  const colors = {
    satisfied: ['var(--green-dim)', 'var(--green)'],
    partial: ['var(--yellow-dim)', 'var(--yellow)'],
    failed: ['var(--red-dim)', 'var(--red)'],
    pending: ['var(--accent-dim)', 'var(--color-ink-faint)'],
  };

  return (
    <>
      {groups.map((g) => (
        <div key={g.title} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-ink-faint)', marginBottom: 6 }}>
            {g.title}
          </div>
          {g.items.map((key) => {
            const c = checks[key];
            if (!c) return null;
            const status = c.status || 'pending';
            const [bg, fg] = colors[status];
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 0', borderBottom: '1px solid var(--color-border)' }}>
                <span style={{ width: 18, height: 18, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 800, flexShrink: 0, background: bg, color: fg }}>
                  {icons[status]}
                </span>
                <span style={{ flex: 1, fontSize: '0.75rem', color: 'var(--color-ink-soft)', lineHeight: 1.2 }}>{c.label}</span>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-ink)', minWidth: 28, textAlign: 'right' }}>{c.earned}/{c.max}</span>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

function BackIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
